import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Editor from 'react-simple-code-editor';
import hljs from 'highlight.js/lib/core';
import xml from 'highlight.js/lib/languages/xml';
import 'highlight.js/styles/github.css';
import { useAdminUpsertEmailFlowStepMutation, useAdminSendTestEmailFlowStepMutation, useMyDataQuery } from '@/generated/graphql';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

hljs.registerLanguage('xml', xml);

type StepLite = {
  id: string;
  order: number;
  delay_minutes: number;
  subject_pt: string;
  body_pt: string;
  subject_en?: string | null;
  body_en?: string | null;
  is_active: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  flowId: string;
  step: StepLite | null;
  nextOrder: number;
  variables: string[];
  onSaved: () => void;
};

const SAMPLE_CONTEXT: Record<string, unknown> = {
  user: { name: 'Sample Name', email: 'sample@example.com' },
  app_url: import.meta.env.VITE_APP_PUBLIC_URL || 'https://app.autoclipper.live',
};

const getByPath = (obj: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);

const substitutePreview = (template: string, declared: string[]): string => {
  if (!template) return '';
  const declaredSet = new Set(declared);
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
    if (!declaredSet.has(key)) return '';
    const value = getByPath(SAMPLE_CONTEXT, key);
    return value == null ? '' : String(value);
  });
};

const HtmlEditor = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <div className="border rounded bg-white overflow-auto html-editor" style={{ height: '480px' }}>
    <Editor
      value={value}
      onValueChange={onChange}
      highlight={(code) => hljs.highlight(code || '', { language: 'xml' }).value}
      padding={12}
      preClassName="hljs"
      textareaClassName="html-editor-textarea focus:outline-none"
      style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        minHeight: '100%',
        color: '#24292e',
      }}
    />
  </div>
);

const HtmlPreview = ({ html }: { html: string }) => {
  const url = useMemo(() => {
    if (!html) return null;
    return URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  }, [html]);

  useEffect(() => {
    if (!url) return undefined;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return (
    <iframe
      title="email-preview"
      className="w-full border rounded bg-white"
      style={{ height: '560px' }}
      src={url || 'about:blank'}
    />
  );
};

const StepEditorDialog = ({ open, onOpenChange, flowId, step, nextOrder, variables, onSaved }: Props) => {
  const { t } = useTranslation();
  const { data: myData } = useMyDataQuery();
  const adminEmail = myData?.myData?.email ?? '';
  const [delay, setDelay] = useState(0);
  const [subjectPt, setSubjectPt] = useState('');
  const [bodyPt, setBodyPt] = useState('');
  const [subjectEn, setSubjectEn] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testLocale, setTestLocale] = useState<'pt' | 'en'>('pt');

  useEffect(() => {
    if (!open) return;
    setDelay(step?.delay_minutes ?? 0);
    setSubjectPt(step?.subject_pt ?? '');
    setBodyPt(step?.body_pt ?? '');
    setSubjectEn(step?.subject_en ?? '');
    setBodyEn(step?.body_en ?? '');
    setIsActive(step?.is_active ?? true);
    setTestEmail(adminEmail);
  }, [open, step, adminEmail]);

  const [upsert, { loading: saving }] = useAdminUpsertEmailFlowStepMutation();
  const [sendTest, { loading: sending }] = useAdminSendTestEmailFlowStepMutation();

  const handleSave = async () => {
    try {
      await upsert({
        variables: {
          input: {
            id: step?.id,
            flow: flowId,
            order: step?.order ?? nextOrder,
            delay_minutes: delay,
            subject_pt: subjectPt,
            body_pt: bodyPt,
            subject_en: subjectEn || null,
            body_en: bodyEn || null,
            is_active: isActive,
          },
        },
      });
      toast.success('Saved');
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleTestSend = async () => {
    if (!step?.id) {
      toast.error('Save the step first before sending a test.');
      return;
    }
    try {
      await sendTest({ variables: { stepId: step.id, locale: testLocale, recipientEmail: testEmail } });
      toast.success(t('admin.emailFlows.testSend.success'));
    } catch (e: any) {
      toast.error(t('admin.emailFlows.testSend.failure') + ': ' + e.message);
    }
  };

  const copyVariable = async (v: string) => {
    await navigator.clipboard.writeText(`{{${v}}}`);
    toast.success(t('admin.emailFlows.steps.copied'));
  };

  const previewBodyPt = substitutePreview(bodyPt, variables);
  const previewBodyEn = substitutePreview(bodyEn || bodyPt, variables);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{step ? t('admin.emailFlows.steps.edit') : t('admin.emailFlows.steps.add')}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.emailFlows.steps.delayMinutes')}</Label>
              <Input type="number" min={0} value={delay} onChange={(e) => setDelay(parseInt(e.target.value, 10) || 0)} />
              <p className="text-xs text-muted-foreground mt-1">{t('admin.emailFlows.steps.delayHelper')}</p>
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>{t('admin.emailFlows.fields.isActive')}</Label>
            </div>
          </div>

          <Tabs defaultValue="pt">
            <TabsList>
              <TabsTrigger value="pt">{t('admin.emailFlows.steps.tabPt')}</TabsTrigger>
              <TabsTrigger value="en">{t('admin.emailFlows.steps.tabEn')}</TabsTrigger>
            </TabsList>
            <TabsContent value="pt" className="space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label>{t('admin.emailFlows.steps.subject')}</Label>
                    <Input value={subjectPt} onChange={(e) => setSubjectPt(e.target.value)} />
                  </div>
                  <div>
                    <Label>{t('admin.emailFlows.steps.body')}</Label>
                    <HtmlEditor value={bodyPt} onChange={setBodyPt} />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>{t('admin.emailFlows.steps.previewSubject')}</Label>
                    <div className="text-sm font-medium border rounded px-3 py-2 bg-muted/30 truncate">
                      {substitutePreview(subjectPt, variables) || <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                  <div>
                    <Label>{t('admin.emailFlows.steps.preview')}</Label>
                    <HtmlPreview html={previewBodyPt} />
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="en" className="space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label>{t('admin.emailFlows.steps.subject')}</Label>
                    <Input value={subjectEn} onChange={(e) => setSubjectEn(e.target.value)} />
                  </div>
                  <div>
                    <Label>{t('admin.emailFlows.steps.body')}</Label>
                    <HtmlEditor value={bodyEn} onChange={setBodyEn} />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>{t('admin.emailFlows.steps.previewSubject')}</Label>
                    <div className="text-sm font-medium border rounded px-3 py-2 bg-muted/30 truncate">
                      {substitutePreview(subjectEn || subjectPt, variables) || <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                  <div>
                    <Label>{t('admin.emailFlows.steps.preview')}</Label>
                    <HtmlPreview html={previewBodyEn} />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <Label>{t('admin.emailFlows.steps.variablesAvailable')}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {variables.map((v) => (
                <Badge key={v} className="cursor-pointer" onClick={() => copyVariable(v)}>{`{{${v}}}`}</Badge>
              ))}
              {variables.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">{t('admin.emailFlows.testSend.title')}</h4>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label>{t('admin.emailFlows.testSend.email')}</Label>
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <Label>{t('admin.emailFlows.testSend.locale')}</Label>
                <Select value={testLocale} onValueChange={(v) => setTestLocale(v as 'pt' | 'en')}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt">PT</SelectItem>
                    <SelectItem value="en">EN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleTestSend} disabled={sending || !testEmail || !step?.id}>
                {t('admin.emailFlows.testSend.send')}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StepEditorDialog;
